/**
 * Où en est la réception, au-dessus du comparateur — chunk 9.AH.1.
 *
 * ## Pourquoi c'est un bandeau, et pas des colonnes
 *
 * Un assureur qu'on attend n'a **aucune colonne** dans la matrice : une colonne
 * vide se lit « aucune garantie offerte », et cette lecture est indétectable — rien
 * à l'écran ne dirait qu'elle est fausse. Les assureurs sans proposition vivent
 * donc ici, à côté du tableau, jamais dedans.
 *
 * ## Trois niveaux, trois endroits
 *
 *   réception  — ce bandeau : a-t-on quelque chose de cet assureur ?
 *   extraction — ce bandeau aussi : ce qu'on a est-il exploitable ?
 *   cellule    — la matrice : que dit ce champ précis ? (`CellStatusBadge`, 9.AB.1)
 *
 * Rien n'est recalculé : `reception_state` arrive qualifié du serveur. Le refaire
 * ici installerait une seconde définition de « prêt à comparer ».
 */
import type { ComparisonResponse } from "@kollectivia/shared";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

type Insurer = NonNullable<ComparisonResponse["insurers"]>[number];

/**
 * Le vert dit « exploitable », l'ambre « il manque quelque chose de notre côté »,
 * l'ardoise « on attend l'assureur », le rouge « on ne peut rien en tirer ».
 *
 * `declined` est en ardoise et non en rouge : refuser de soumettre est une réponse,
 * pas une panne.
 */
const TON: Record<string, string> = {
  ready: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  needs_review: "bg-amber-50 text-amber-800 ring-amber-200",
  extracting: "bg-blue-50 text-blue-800 ring-blue-200",
  extraction_failed: "bg-red-50 text-red-800 ring-red-200",
  awaiting: "bg-slate-100 text-slate-700 ring-slate-200",
  declined: "bg-slate-100 text-slate-600 ring-slate-200",
  expired: "bg-slate-100 text-slate-600 ring-slate-200",
};

/** L'ordre de lecture : ce sur quoi on peut agir d'abord. */
const ORDRE: readonly string[] = [
  "extraction_failed",
  "needs_review",
  "extracting",
  "awaiting",
  "declined",
  "expired",
  "ready",
];

export function InsurerReceptionBar({ data }: { readonly data: ComparisonResponse }) {
  const { t } = useTranslation();

  // Charge antérieure à ce chunk : le bandeau s'efface plutôt que d'afficher des
  // compteurs à zéro qui se liraient « aucun assureur sollicité ».
  const insurers = Array.isArray(data.insurers)
    ? data.insurers.filter((i) => i?.recipient_id)
    : null;
  if (insurers === null || insurers.length === 0) return null;

  const tries = [...insurers].sort((a, b) => {
    const d = ORDRE.indexOf(a.reception_state) - ORDRE.indexOf(b.reception_state);
    return d !== 0 ? d : a.insurer_code.localeCompare(b.insurer_code);
  });

  const attendus = insurers.filter((i) => i.reception_state === "awaiting").length;
  const comparables = insurers.filter((i) => i.reception_state === "ready").length;
  const aVerifier = insurers.filter((i) => i.reception_state === "needs_review").length;

  return (
    <section
      aria-label={t("reception.title")}
      className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-900">{t("reception.title")}</h2>
        <p className="text-sm text-slate-600">
          {t("reception.summary", { comparable: comparables, total: insurers.length })}
        </p>
      </div>

      <details className="mt-2 text-sm text-slate-600">
        <summary className="cursor-pointer underline">{t("reception_flow.help")}</summary>
        <p className="mt-2">{t("reception.scope")}</p>
        <p className="mt-1">{t("reception.next")}</p>
      </details>
      <Link
        to={
          data.quote_request_id
            ? `/quote-requests/${data.quote_request_id}/parcours`
            : `/projects/${data.project_id}#quote-requests`
        }
        className="text-brand-700 focus-visible:ring-brand-500 mt-2 inline-block rounded text-sm font-medium underline focus-visible:outline-none focus-visible:ring-2"
      >
        {t("reception.open_requests")}
      </Link>

      {aVerifier > 0 && (
        <p className="mt-1 text-sm text-amber-800">
          {t("reception.review_summary", { count: aVerifier })}
        </p>
      )}

      {attendus > 0 && (
        // Le dire explicitement : sans cette phrase, un tableau à deux colonnes se
        // lit « voici les offres du marché » alors qu'il en manque trois.
        <p className="mt-1 text-sm font-medium text-slate-800">
          {t("reception.incomplete", { count: attendus })}
        </p>
      )}

      <ul className="mt-3 flex flex-wrap gap-2">
        {tries.map((i) => (
          <Pastille key={i.recipient_id} item={i} />
        ))}
      </ul>
    </section>
  );
}

function Pastille({ item }: { readonly item: Insurer }) {
  const { t } = useTranslation();
  const dansLaMatrice = item.has_column === true;

  return (
    <li
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ring-1 ring-inset ${
        TON[item.reception_state] ?? TON.awaiting
      }`}
    >
      <span className="font-medium" translate="no">
        {item.insurer_code}
      </span>
      <span>{t(`reception.state.${item.reception_state}`)}</span>
      {item.submission_id !== null && !dansLaMatrice && (
        // Proposition reçue mais hors de CETTE matrice : filtre par demande, ou
        // proposition détachée. Sans le dire, on chercherait une colonne absente.
        <span className="text-slate-500">{t("reception.not_in_view")}</span>
      )}
    </li>
  );
}
